import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, firstValueFrom, from, Observable, of, throwError } from 'rxjs';
import { catchError, filter, retry } from 'rxjs/operators';
import { ERRORS, TOAST_CLASSES } from 'src/app/common//utils/notification-constants';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { CustomErrorCodes, CustomErrorMessages } from 'src/custom_error_codes.constants';
import { UpgradeVersionNotificationComponent } from './popup-notifications/upgrade-version-notification/upgrade-version-notification.component';
import { AuthService } from './services/auth.service';
import { ModalBackdropPreset, ModalManagerService } from './services/modal-manager.service';
import { UserService } from './services/user.service';
import { HomeNavigationService } from './services/home-navigation.service';
import { TelemetryService } from './services/telemetry.service';
import { modifiedSetTimeout, modifiedTimer } from './utilities/ZoneUtils';
import { ErrorInterceptorSkipHeader } from './common/functions-and-constant/interceptor';
import { AuthTracing } from './utilities/auth-tracing.utils';

// To be added as a header in request we do not want their error to be intercepted. But sent as received.

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private DEFAULT_ERROR_TITLE_MSG = this.translateService.instant('Something went wrong!');
  private DEFAULT_ERROR_CONTENT_MSG = this.translateService.instant(
    'We’re sorry, but we could not complete your request. Please check your internet connection and try again later.',
  );
  private MAX_RETRY_ATTEMPTS = 5;
  private EXP_BACKOFF_SCALING_FACTOR = 600;
  private TIMEOUT_ERROR_CODES = [0, 504, 408];

  private readonly MONGO_DB_ID_REGEX = '[0-9a-fA-F]{24}';
  private readonly retryUrlPatterns: RegExp[] = [
    /\/tutor\/messages\/search\//,
    /\/utils\/time/,
    /\/attendance\/host\/analytics\/\?days=\d{1,5}/,
    new RegExp(`/spaces/${this.MONGO_DB_ID_REGEX}/event/\\?time=\\d{1,5}`),
    new RegExp(`/attendance/attendee/${this.MONGO_DB_ID_REGEX}`),
    new RegExp(`/attendance/host/${this.MONGO_DB_ID_REGEX}`),
    new RegExp(`/attendance/${this.MONGO_DB_ID_REGEX}`),
    /\/analytics\/sessions\/details/,
  ];

  constructor(
    private router: Router,
    private modalManagerService: ModalManagerService,
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private authService: AuthService,
    private userService: UserService,
    private telemetryService: TelemetryService,
    private homeNavigation: HomeNavigationService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    /** If ErrorInterceptorSkipHeader header is present. Don't intercept error.
     Return them as received so they can be handled where they were initiated. */
    if (request.headers.has(ErrorInterceptorSkipHeader)) {
      const headers = request.headers.delete(ErrorInterceptorSkipHeader);
      return next.handle(request.clone({ headers })).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401 && error.error?.error_code === 'INVALID_AUTH_TOKEN') {
            AuthTracing.logWithTokenContext(
              'ErrorInterceptor.skip_header.401_INVALID_AUTH_TOKEN',
              this.authService.getCurrentUser(),
              { url: error.url },
            );
          }
          return throwError(() => error);
        }),
      );
    }
    return next.handle(request).pipe(
      retry({
        count: this.MAX_RETRY_ATTEMPTS,
        delay: (err: HttpErrorResponse, index) => {
          // TODO: remove the URL guard condition when we're sure about the interceptor
          if (!this.TIMEOUT_ERROR_CODES.includes(err.status)) {
            throw err;
          } else {
            // TODO: remove the URL guard condition when we're sure about the interceptor
            if (this.isRetryURL(err.url)) {
              // Log API retries in the form [URL][StatusCode]
              this.telemetryService.log(
                'warn',
                `[API Retry] Retrying request: [count: ${index}] [${err.status}] [${err.url}]`,
              );
              const backOffTime = Math.pow(2, index - 1) * this.EXP_BACKOFF_SCALING_FACTOR;
              return modifiedTimer(backOffTime);
            } else {
              this.telemetryService.log(
                'error',
                `[API Retry] Not retrying request: [${err.status}] [${err.url}]`,
              );
              throw err;
            }
          }
        },
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Only retry for token-expired/revoked/invalid cases from backend
          if (error.error?.error_code === 'INVALID_AUTH_TOKEN') {
            AuthTracing.logWithTokenContext(
              'ErrorInterceptor.401_INVALID_AUTH_TOKEN',
              this.authService.getCurrentUser(),
              { url: error.url },
            );
            return from(this.handleTokenExpiredRetry(request, next, error));
          }
          // Other 401s (e.g. account disabled, no token) — original behavior
          AuthTracing.log('ErrorInterceptor.401_other', { url: error.url, err: error.error?.err });
          this.showErrorNotification(
            error.error?.err
              ? this.translateService.instant(error.error.err)
              : this.DEFAULT_ERROR_CONTENT_MSG,
          );
          return throwError(() => error);
        } else if (this.TIMEOUT_ERROR_CODES.includes(error.status)) {
          // TODO: remove the guard condition when we're sure about the interceptor
          if (
            error.url?.includes('tutor/messages/search') ||
            error.url?.includes('analytics/sessions/details')
          ) {
            this.showDetailedErrorNotification(
              this.DEFAULT_ERROR_TITLE_MSG,
              this.DEFAULT_ERROR_CONTENT_MSG,
              ERRORS.NETWORK_DISCONNECTED,
            );
          }
          return throwError(() => error);
        } else if (error?.error?.status_code === CustomErrorCodes.BYPASS_ERROR_INTERCEPTOR) {
          return throwError(() => error);
        } else if (error?.error?.status_code === CustomErrorCodes.CLIENT_VERSION_UPDATE_NEEDED) {
          // Release version smaller than min FE version
          this.modalManagerService.showModal(UpgradeVersionNotificationComponent, {
            width: '320px',
            panelClass: 'upgrade-version-modal',
            ...this.modalManagerService.getBackdropPreset(ModalBackdropPreset.BLOCKING),
          });
        } else if (
          error.error?.err === CustomErrorMessages.SPACE_NOT_FOUND ||
          error.error?.err === CustomErrorMessages.INVALID_SPACE_ID
        ) {
          this.showSpaceDoesNotExistNotification();
        } else if (error.error?.status_code === CustomErrorCodes.FB_EMAIL_NOT_IN_TOKEN) {
          AuthTracing.log('ErrorInterceptor.FB_EMAIL_NOT_IN_TOKEN');
          this.authService.signout();
          this.showEmailNotInUserNotification();
          this.userService.appLoading.next(false);
        } else if (
          error.error?.error_code === CustomErrorCodes.CANNOT_CLONE_SPACE_TEMPLATE ||
          error.error?.error_code === CustomErrorCodes.SPACE_IS_NOT_TEMPLATE
        ) {
          this.showCannotCloneSpaceTemplateNotification();
        } else if (
          !this.router.url.includes('print') &&
          error.error?.err &&
          !error.error.err?.includes('Message not found')
        ) {
          this.showErrorNotification(this.translateService.instant(error.error.err));
        } else if (error?.status === 404 && error?.error?.error_code === 5001) {
          // Vimeo error, video not found because not owned by us
          // Pass it for handling in the vimeo-upload service.
          return throwError(() => error);
        } else if (error?.status === 500 && error?.url?.includes('pencil-ai-assistant')) {
          // Pass it for handling in the pencil-ai-assistant service.
          return throwError(() => error);
        }
        // Do not throw an error here otherwise sentry will capture and report it.
        return of<HttpEvent<any>>();
      }),
    );
  }

  /**
   * Attempt to refresh the Firebase ID token and retry the request once.
   * If refresh fails or retry fails, redirect to login page.
   */
  private async handleTokenExpiredRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse,
  ): Promise<HttpEvent<any>> {
    try {
      const user = this.authService.getCurrentUser();
      if (!user) {
        AuthTracing.log('handleTokenExpiredRetry.no_user', { url: request.url });
        this.redirectToLogin();
        return EMPTY as unknown as HttpEvent<any>;
      }
      AuthTracing.log('handleTokenExpiredRetry.refreshing_token', { url: request.url });
      const newToken = await user.getIdToken(true);
      const retryReq = request.clone({
        setHeaders: { Authorization: `Bearer ${newToken}` },
      });
      // Wait for the final response. HttpClient emits HttpSentEvent first; without this
      // filter firstValueFrom resolves on that early event and aborts the retry (canceled XHR).
      const result = await firstValueFrom(
        next
          .handle(retryReq)
          .pipe(
            filter((event): event is HttpResponse<any> => event.type === HttpEventType.Response),
          ),
      );
      AuthTracing.log('handleTokenExpiredRetry.retry_success', { url: request.url });
      return result;
    } catch (retryError) {
      AuthTracing.log('handleTokenExpiredRetry.retry_failed', {
        url: request.url,
        error: retryError instanceof Error ? retryError.message : String(retryError),
      });
      this.redirectToLogin();
      return EMPTY as unknown as HttpEvent<any>;
    }
  }

  private redirectToLogin(): void {
    this.authService.signoutAndRedirect();
  }

  showCannotCloneSpaceTemplateNotification(): void {
    this.showDetailedErrorNotification(
      this.translateService.instant('Template not found'),
      this.translateService.instant('Please verify the template link is correct.'),
      ERRORS.TEMPLATE_NOT_FOUND,
    );
    // Redirecting immediately after showing a notification will crash the page,
    modifiedSetTimeout(() => {
      this.homeNavigation.hardRedirect();
    }, 3500);
  }

  showSpaceDoesNotExistNotification() {
    this.showDetailedErrorNotification(
      this.translateService.instant('Space not found'),
      this.translateService.instant('Please check your Space URL and try again.'),
      ERRORS.SPACE_NOT_FOUND,
    );
  }

  showEmailNotInUserNotification() {
    this.showDetailedErrorNotification(
      this.translateService.instant('Facebook login failed'),
      this.translateService.instant(
        'Your Facebook account needs an email address. Please add one or use another login method.',
      ),
      ERRORS.UNSUPPORTED_FB_ACCOUNT,
    );
  }

  isRetryURL(url: string | null): boolean {
    if (!url) {
      return false;
    }
    return this.retryUrlPatterns.some((pattern) => pattern.test(url));
  }

  /**
   * Utility method to show v2 error notification with onActivateTick enabled and high z-index
   * This ensures notifications appear without needing manual Change Detection and above most UI elements
   */
  private showDetailedErrorNotification(
    title: string,
    description: string,
    errorCode: string,
  ): void {
    this.notificationToasterService.showDefaultErrorNotificationV2(
      title,
      description,
      errorCode,
      undefined, // notificationIconConfig - use default
      undefined, // buttons - none
      undefined, // timeOut - use default
      true, // onActivateTick - set to true to avoid CD issues
      undefined, // onTimedOut - default
      TOAST_CLASSES.HIGH_Z_INDEX, // toastClass - high z-index to appear above most UI elements
    );
  }

  /**
   * Utility method to show simple v2 error notification with default title and random code
   */
  private showErrorNotification(description: string): void {
    this.showDetailedErrorNotification(
      this.DEFAULT_ERROR_TITLE_MSG,
      description,
      this.notificationToasterService.generateNotificationCode(),
    );
  }
}
